import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  CircleAlert,
  CreditCard,
  Database,
  FileText,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { SPACING } from "../../common/constants/spacing";
import { useAuth } from "../../common/contexts/AuthContext";
import { deleteAccount } from "../api/user";

function DeleteAccount() {
  const { t } = useTranslation("account");
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDeleteAccount = async () => {
    setLoading(true);
    setError("");

    try {
      await deleteAccount();
      logout();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message || t("deleteAccount.error"));
    } finally {
      setLoading(false);
      setConfirmationOpen(false);
    }
  };

  const dataLossItems = [
    {
      icon: <Database size={20} />,
      title: t("deleteAccount.dataLoss.projects.title"),
      description: t("deleteAccount.dataLoss.projects.description"),
    },
    {
      icon: <Users size={20} />,
      title: t("deleteAccount.dataLoss.teams.title"),
      description: t("deleteAccount.dataLoss.teams.description"),
    },
    {
      icon: <FileText size={20} />,
      title: t("deleteAccount.dataLoss.reports.title"),
      description: t("deleteAccount.dataLoss.reports.description"),
    },
    {
      icon: <CreditCard size={20} />,
      title: t("deleteAccount.dataLoss.billing.title"),
      description: t("deleteAccount.dataLoss.billing.description"),
    },
  ];

  return (
    <Box>
      <PageHeader title={t("deleteAccount.title")} />

      <Alert severity="error" variant="filled" sx={{ mb: SPACING.xl }}>
        <Typography
          variant="bodyMd"
          component="p"
          sx={{ fontWeight: 600, mb: SPACING.xs }}
        >
          {t("deleteAccount.warningTitle")}
        </Typography>
        <Typography variant="bodyMd">
          {t("deleteAccount.warningMessage")}
        </Typography>
      </Alert>

      <Typography variant="h3" sx={{ mb: SPACING.sm }}>
        {t("deleteAccount.whatWillBeDeleted")}
      </Typography>

      <List sx={{ mb: 3 }}>
        {dataLossItems.map((item, index) => (
          <Box key={index}>
            <ListItem sx={{ px: 0, py: 2 }}>
              <ListItemIcon
                sx={{ minWidth: 40, color: "error.main", pr: SPACING.md }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {item.title}
                  </Typography>
                }
                secondary={
                  <Typography variant="bodySm" color="text.secondary">
                    {item.description}
                  </Typography>
                }
              />
            </ListItem>
            {index < dataLossItems.length - 1 && <Divider />}
          </Box>
        ))}
      </List>

      {user?.subscription_plan !== "free" && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="bodySm">
            <strong>{t("deleteAccount.subscriptionNotice")}</strong>{" "}
            {t("deleteAccount.subscriptionMessage", {
              plan: user?.subscription_plan,
            })}
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="bodySm">{error}</Typography>
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          variant="secondary"
          onClick={() => navigate("/account")}
          disabled={loading}
        >
          {t("deleteAccount.cancel")}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => setConfirmationOpen(true)}
          disabled={loading}
        >
          {t("deleteAccount.deleteButton")}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <CircleAlert size={24} color="#f44336" style={{ marginRight: 12 }} />
          {t("deleteAccount.confirmDialog.title")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="bodyMd" component="p" sx={{ mb: SPACING.sm }}>
            {t("deleteAccount.confirmDialog.message")}
          </Typography>
          <Typography variant="bodySm" color="text.secondary">
            {t("deleteAccount.confirmDialog.details", { email: user?.email })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setConfirmationOpen(false)} disabled={loading}>
            {t("deleteAccount.confirmDialog.cancel")}
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading
              ? t("deleteAccount.confirmDialog.deleting")
              : t("deleteAccount.confirmDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeleteAccount;
